import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  confirm,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@devhop/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ExternalLinkIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import {
  useDeleteRoomDisplay,
  useRoomDisplaysQuery,
} from "../api/queries";

export function RoomDisplaysPage() {
  const nav = useNavigate({ from: "/app/room-displays" });
  const list = useRoomDisplaysQuery();
  const canUpdate = useActionPermission("room-displays:update");
  const deleteMutation = useDeleteRoomDisplay();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຈໍສະແດງຜົນ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="font-semibold text-xl">ຈໍສະແດງຜົນ</h1>
            <p className="text-muted-foreground text-sm">
              ຈໍ TV — ຕາຕະລາງລວມທຸກຫ້ອງ ແລະ poster
            </p>
          </div>
          {canUpdate ? (
            <Button onClick={() => nav({ to: "/app/room-displays/new" })}>
              <PlusIcon className="size-4" />
              ສ້າງຈໍໃໝ່
            </Button>
          ) : null}
        </div>

        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ຊື່</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Poster</TableHead>
                <TableHead>ສະຖານະ</TableHead>
                <TableHead className="w-[1%] text-right">ການດຳເນີນການ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    ກຳລັງໂຫຼດ…
                  </TableCell>
                </TableRow>
              ) : (list.data?.data.length ?? 0) === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    ຍັງບໍ່ມີຈໍສະແດງຜົນ
                  </TableCell>
                </TableRow>
              ) : (
                list.data?.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link
                        to="/app/room-displays/$id"
                        params={{ id: row.id }}
                        className="font-medium hover:underline"
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{row.slug}</TableCell>
                    <TableCell>{row.posterCount}</TableCell>
                    <TableCell>
                      <Badge variant={row.isActive ? "default" : "secondary"}>
                        {row.isActive ? "ເປີດໃຊ້" : "ປິດ"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="ແກ້ໄຂ"
                          onClick={() =>
                            nav({
                              to: "/app/room-displays/$id",
                              params: { id: row.id },
                            })
                          }
                        >
                          <PencilIcon className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="ເປີດຈໍສະແດງຜົນ"
                          onClick={() =>
                            window.open(
                              `/display/${row.slug}`,
                              "_blank",
                              "noopener",
                            )
                          }
                        >
                          <ExternalLinkIcon className="size-4" />
                        </Button>
                        {canUpdate ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="ລຶບ"
                            onClick={async () => {
                              const ok = await confirm({
                                title: "ລຶບຈໍສະແດງຜົນ",
                                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${row.name}"?`,
                                actionText: "ລຶບ",
                                ActionProps: {
                                  variant: "destructive",
                                },
                              });
                              if (!ok) return;
                              toast.promise(deleteMutation.mutateAsync(row.id), {
                                loading: "ກຳລັງລຶບ…",
                                success: "ລຶບສຳເລັດ",
                                error: "ລຶບບໍ່ສຳເລັດ",
                              });
                            }}
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Main>
    </>
  );
}
